import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Check, Plus, X } from 'lucide-react';
import { RootState } from '../../../redux/rootReducer';
import { setCart } from '../../../redux/reducers/userSlice';
import UserService from '../../../services/user.service';
import AuthPrimaryButton from '../../../components/ui/AuthPrimaryButton';
import {
    modalAlert,
    modalBody,
    modalCloseBtn,
    modalField,
    modalLabel,
    modalNotice,
    modalSecondaryBtn,
    modalSubtitle,
    modalTitle,
} from '../../../components/ui/modalDesign';
import { formatCurrency } from '../../../components/custom-hooks';
import { isAfricanCountry } from '../../../utils/constants';
import { useRegistration } from '../../../components/layouts/app/RegistrationContext';
import {
    convertTimeGroupSlotsToTimezone,
    filterDaysOfTheWeek,
    restructureTimeData,
} from '../../../utils/Tools';

export const REOPEN_ADHOC_SESSION_KEY = 'ryd-reopen-adhoc';

interface Props {
    adhocPackage: any;
    onClose: () => void;
}

export default function AdhocRegisterModal({ adhocPackage, onClose }: Props) {
    const userInfo: any = useSelector((state: RootState) => state.auth.userInfo);
    const currencyInfo = useSelector((state: RootState) => state.user.currency);
    const dispatch = useDispatch();
    const userService = new UserService();
    const { openAddChildOnly } = useRegistration();

    const [children, setChildren] = useState<any[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);

    const [dayTime, setDayTime] = useState<any[]>([]);
    const [dayArr, setDayArr] = useState<{ name: string; value: number }[]>([]);
    const [timeArr, setTimeArr] = useState<{ name: string; value: number }[]>([]);
    const [selectedDay, setSelectedDay] = useState<{ name: string; value: number } | null>(null);
    const [selectedTime, setSelectedTime] = useState<{ name: string; value: number } | null>(null);

    const loadChildren = async () => {
        setLoading(true);
        try {
            const response = await userService.getAllChildren();
            if (!response?.status) {
                setChildren([]);
                return;
            }
            setChildren(Array.isArray(response.data) ? response.data : []);
        } catch (err: any) {
            toast.error(err?.message || 'Unable to load children');
            setChildren([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadChildren();
    }, []);

    // Build day/time options from time group, converted to the parent's timezone
    useEffect(() => {
        const rawSlots = adhocPackage?.timeGroup?.times;
        if (!rawSlots) {
            setDayTime([]);
            setDayArr([]);
            setSelectedDay(null);
            setSelectedTime(null);
            return;
        }
        const converted = convertTimeGroupSlotsToTimezone(rawSlots, userInfo?.timezone);
        const remTimeData = restructureTimeData(converted as any);
        setDayTime(remTimeData);
        setDayArr(filterDaysOfTheWeek(remTimeData));
        setSelectedDay(null);
        setSelectedTime(null);

        // Auto-select when there is only one day / one time placement
        if (remTimeData.length === 1) {
            const onlyDay = { name: remTimeData[0].dayText, value: remTimeData[0].day };
            setSelectedDay(onlyDay);
            if (remTimeData[0].times?.length === 1) {
                setSelectedTime({
                    name: remTimeData[0].times[0].timeText,
                    value: remTimeData[0].times[0].time,
                });
            }
        }
    }, [adhocPackage?.timeGroup?.times, userInfo?.timezone]);

    useEffect(() => {
        if (!selectedDay) {
            setTimeArr([]);
            return;
        }
        const timeX = dayTime?.filter((item: any) => item.dayText === selectedDay.name);
        const tdx = timeX?.[0]?.times || [];
        const arr = tdx.map((t: any) => ({ name: t.timeText, value: t.time }));
        setTimeArr(arr);
        // Keep auto-selected time if it still exists for this day
        if (arr.length === 1) {
            setSelectedTime(arr[0]);
        } else if (selectedTime && !arr.some((t: any) => t.value === selectedTime.value)) {
            setSelectedTime(null);
        }
    }, [selectedDay, dayTime]);

    const goAddChild = () => {
        try {
            sessionStorage.setItem(REOPEN_ADHOC_SESSION_KEY, '1');
        } catch {
            /* ignore */
        }
        onClose();
        openAddChildOnly();
    };

    const displayPrice = useMemo(() => {
        const country = userInfo?.country;
        const amount = Number(adhocPackage?.amount) || 0;
        const altAmount = Number(adhocPackage?.altAmount) || 0;
        const code = currencyInfo?.currencyCode || (isAfricanCountry(country) ? 'NGN' : 'CAD');
        const base = isAfricanCountry(country) ? altAmount : amount;
        const rate = currencyInfo?.useRate ? (currencyInfo?.rate || 1) : 1;
        return `${code} ${formatCurrency(base * rate)}`;
    }, [adhocPackage, currencyInfo, userInfo?.country]);

    const childEligibility = (child: any) => {
        const age = Number(child?.age);
        const minAge = Number(adhocPackage?.minAge);
        const maxAge = Number(adhocPackage?.maxAge);
        if (!Number.isNaN(age) && (age < minAge || age > maxAge)) {
            return { ok: false, reason: `Age ${age} is outside ${minAge}-${maxAge}` };
        }
        const programs = Array.isArray(child?.programs) ? child.programs : [];
        const blocked = programs.some(
            (p: any) => Boolean(p?.package?.isAdhoc) && !p?.isCompleted
        );
        if (blocked) {
            return { ok: false, reason: 'Already has an active adhoc enrollment' };
        }
        return { ok: true, reason: '' };
    };

    const toggleChild = (id: number, eligible: boolean) => {
        if (!eligible) return;
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const removeSelected = (id: number) => {
        setSelectedIds((prev) => prev.filter((x) => x !== id));
    };

    const canProceed = selectedIds.length > 0 && !!selectedDay && !!selectedTime;

    const handleProceed = async () => {
        if (!selectedIds.length) {
            toast.warn('Select at least one child');
            return;
        }
        if (!selectedDay || !selectedTime) {
            toast.warn('Please select a lesson day and time');
            return;
        }
        if (!adhocPackage?.timeGroup) {
            toast.error('This adhoc package has no time group configured');
            return;
        }
        setSubmitLoading(true);
        try {
            const response = await userService.registerAdhoc({
                childIds: selectedIds,
                timeOffset: Number(userInfo?.timeOffset) || 0,
                day: Number(selectedDay.value),
                time: Number(selectedTime.value),
            });
            if (!response?.status) {
                toast.error(response?.message || 'Unable to add adhoc package to cart');
                return;
            }
            if (Array.isArray(response?.data?.skipped) && response.data.skipped.length > 0) {
                toast.warn(
                    `${response.data.created?.length || 0} added, ${response.data.skipped.length} skipped`
                );
            } else {
                toast.success(response.message || 'Added to cart');
            }
            dispatch(setCart(true));
            window.dispatchEvent(new Event('ryd-open-cart'));
            onClose();
        } catch (err: any) {
            toast.error(err?.message || 'Unable to add adhoc package to cart');
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <div className={`relative ${modalBody}`}>
            <button type="button" aria-label="Close" className={modalCloseBtn} onClick={onClose}>
                <X size={18} />
            </button>

            <div className="mb-5 pr-8">
                <p className="text-[12px] font-semibold uppercase tracking-wide text-ryd-primary">
                    Adhoc package
                </p>
                <h1 className={modalTitle}>{adhocPackage?.title || adhocPackage?.name}</h1>
                <p className={modalSubtitle}>{adhocPackage?.description}</p>
            </div>

            <div className="mb-5 grid gap-3 rounded-[16px] border border-[#efe6f1] bg-[#FBF8FC] p-4 sm:grid-cols-2">
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8b9aab]">Price</p>
                    <p className="mt-0.5 text-[15px] font-semibold text-ryd-headerTextPrimary">{displayPrice}</p>
                </div>
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8b9aab]">Duration</p>
                    <p className="mt-0.5 text-[15px] font-semibold text-ryd-headerTextPrimary">
                        {adhocPackage?.weekDuration || 8} weeks
                    </p>
                </div>
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8b9aab]">Cohort</p>
                    <p className="mt-0.5 text-[15px] font-semibold text-ryd-headerTextPrimary">
                        {adhocPackage?.cohort?.title || '—'}
                    </p>
                </div>
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8b9aab]">Age range</p>
                    <p className="mt-0.5 text-[15px] font-semibold text-ryd-headerTextPrimary">
                        {adhocPackage?.minAge} – {adhocPackage?.maxAge} years
                    </p>
                </div>
            </div>

            <div className={`${modalNotice} mb-4`}>
                Times are shown in your timezone:{' '}
                <span className="font-semibold text-emerald-700">
                    {userInfo?.timeOffset > 0 && '+'}
                    {userInfo?.timezone || 'Africa/Lagos'}
                    {userInfo?.timeOffset !== undefined && userInfo?.timeOffset !== null
                        ? ` (${userInfo.timeOffset})`
                        : ''}
                </span>
            </div>

            {!adhocPackage?.timeGroup ? (
                <div className={`${modalAlert} mb-4`}>
                    This adhoc package has no time group yet. Please contact support or try again later.
                </div>
            ) : (
                <div className="mb-5 grid gap-3 sm:grid-cols-2">
                    <div>
                        <label className={modalLabel}>Lesson day</label>
                        <select
                            className={modalField}
                            value={selectedDay?.value ?? ''}
                            onChange={(e) => {
                                const value = Number(e.target.value);
                                const match = dayArr.find((d) => d.value === value) || null;
                                setSelectedDay(match);
                                setSelectedTime(null);
                            }}
                        >
                            <option value="">Select day</option>
                            {dayArr.map((d) => (
                                <option key={d.value} value={d.value}>
                                    {d.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={modalLabel}>Lesson time</label>
                        <select
                            className={modalField}
                            value={selectedTime?.value ?? ''}
                            disabled={!selectedDay}
                            onChange={(e) => {
                                const value = Number(e.target.value);
                                const match = timeArr.find((t) => t.value === value) || null;
                                setSelectedTime(match);
                            }}
                        >
                            <option value="">Select time</option>
                            {timeArr.map((t) => (
                                <option key={t.value} value={t.value}>
                                    {t.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex h-28 items-center justify-center">
                    <div className="h-10 w-10 animate-spin rounded-full border border-ryd-primary border-l-white" />
                </div>
            ) : children.length === 0 ? (
                <div className={modalNotice}>
                    <p className="font-semibold text-[#7a5c20]">Add a child first</p>
                    <p className="mt-1">
                        You need at least one child on your account before registering for this adhoc package.
                    </p>
                    <button
                        type="button"
                        onClick={goAddChild}
                        className="mt-3 inline-flex cursor-pointer items-center gap-1 text-[13px] font-semibold text-ryd-primary hover:underline"
                    >
                        <Plus size={14} strokeWidth={2.5} />
                        Add new child
                    </button>
                </div>
            ) : (
                <>
                    <div className="mb-2 flex items-center justify-between gap-3">
                        <p className="text-[13px] font-semibold text-ryd-headerTextPrimary">
                            Select children to register
                        </p>
                        <button
                            type="button"
                            onClick={goAddChild}
                            className="inline-flex shrink-0 cursor-pointer items-center gap-1 text-[12px] font-semibold text-ryd-primary hover:underline"
                        >
                            <Plus size={13} strokeWidth={2.5} />
                            Add new child
                        </button>
                    </div>
                    <div className="max-h-[240px] space-y-2 overflow-y-auto pr-1">
                        {children.map((child) => {
                            const eligibility = childEligibility(child);
                            const selected = selectedIds.includes(child.id);
                            return (
                                <button
                                    key={child.id}
                                    type="button"
                                    disabled={!eligibility.ok}
                                    onClick={() => toggleChild(child.id, eligibility.ok)}
                                    className={`flex w-full items-center justify-between rounded-[14px] border px-4 py-3 text-left transition ${
                                        !eligibility.ok
                                            ? 'cursor-not-allowed border-[#eee] bg-[#f8f8f8] opacity-70'
                                            : selected
                                              ? 'border-ryd-primary bg-ryd-primaryLess1/40'
                                              : 'border-[#efe6f1] bg-white hover:border-ryd-primary/50'
                                    }`}
                                >
                                    <div>
                                        <p className="text-[14px] font-semibold capitalize text-ryd-headerTextPrimary">
                                            {child.firstName} {child.lastName}
                                        </p>
                                        <p className="text-[12px] text-[#8b9aab]">
                                            Age {child.age}
                                            {!eligibility.ok ? ` · ${eligibility.reason}` : ''}
                                        </p>
                                    </div>
                                    {selected ? (
                                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ryd-primary text-white">
                                            <Check size={14} />
                                        </span>
                                    ) : (
                                        <span className="h-7 w-7 rounded-full border border-[#d8cfe0]" />
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {selectedIds.length > 0 && (
                        <div className="mt-4">
                            <p className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-[#8b9aab]">
                                Selected ({selectedIds.length})
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {selectedIds.map((id) => {
                                    const child = children.find((c) => c.id === id);
                                    if (!child) return null;
                                    return (
                                        <button
                                            key={id}
                                            type="button"
                                            onClick={() => removeSelected(id)}
                                            className="inline-flex items-center gap-1.5 rounded-full border border-[#efe6f1] bg-white px-3 py-1.5 text-[12px] font-semibold capitalize text-[#476788] hover:border-red-300 hover:text-red-600"
                                        >
                                            {child.firstName}
                                            <X size={12} />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {!canProceed && (
                        <p className={`${modalAlert} mt-4`}>
                            {!selectedDay || !selectedTime
                                ? 'Select a lesson day and time, then choose one or more children.'
                                : 'Select one or more eligible children to continue.'}
                        </p>
                    )}
                </>
            )}

            {children.length > 0 && (
                <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <button type="button" onClick={onClose} className={modalSecondaryBtn}>
                        Cancel
                    </button>
                    <AuthPrimaryButton
                        type="button"
                        loading={submitLoading}
                        disabled={!canProceed}
                        showArrow={false}
                        onClick={handleProceed}
                    >
                        Proceed to cart
                    </AuthPrimaryButton>
                </div>
            )}
        </div>
    );
}
